type AppErrorData = {
  type: Lowercase<string>;
  message: string;
};

/**
 * Represents an error that occurs within the application.
 * Extends the built-in Error class and adds a `type` property for categorization.
 */
export class AppError extends Error {
  /** The type of error (e.g., 'auth', 'server'). */
  public type: string;

  /** A detailed message describing the error. */
  public message: string;

  /**
   * Creates a new AppError instance.
   *
   * @param data - The error data.
   * @param data.type - The type of error.
   * @param data.message - The error message.
   */
  constructor({ type, message }: AppErrorData) {
    super(message);
    this.type = type;
    this.message = message;
  }

  /**
   * Creates an authentication error.
   *
   * @param message - The error message (default: 'Unauthenticated. Login required.').
   * @returns A new AppError instance of type 'auth'.
   */
  static authError(
    message: AppErrorData['message'] = 'Unauthenticated. Login required.',
  ): AppError {
    return new AppError({ type: 'auth', message });
  }

  /**
   * Creates a server error.
   *
   * @param message - The error message (default: 'Error connecting to the server.').
   * @returns A new AppError instance of type 'server'.
   */
  static serverError(
    message: AppErrorData['message'] = 'Error connecting to the server.',
  ): AppError {
    return new AppError({ type: 'server', message });
  }

  static hookScopeError(message: AppErrorData['message']): AppError {
    return new AppError({ type: 'hook-scope', message });
  }
}
